import React, { useState, useRef } from 'react';
import { Modal, Typography, Button } from 'antd';
import { HolderOutlined } from '@ant-design/icons';
import type { ForecastField } from '../../api/forecastFieldsService';
import { getFieldIcon } from './fieldRenderers';

const { Text } = Typography;

interface FieldReorderModalProps {
  open: boolean;
  fields: ForecastField[];
  onSave: (orderedFields: ForecastField[]) => void;
  onCancel: () => void;
  onReset: () => void;
  saving?: boolean;
  title?: string;
}

export const FieldReorderModal: React.FC<FieldReorderModalProps> = ({
  open,
  fields,
  onSave,
  onCancel,
  onReset,
  saving = false,
  title = 'Reorder Fields',
}) => {
  const [reorderFields, setReorderFields] = useState<ForecastField[]>(fields);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const dragNodeRef = useRef<HTMLDivElement | null>(null);

  // Update local state when fields prop changes
  React.useEffect(() => {
    setReorderFields(fields);
  }, [fields]);

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, index: number) => {
    setDraggedIndex(index);
    dragNodeRef.current = e.currentTarget;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index.toString());
    setTimeout(() => {
      if (dragNodeRef.current) {
        dragNodeRef.current.style.opacity = '0.5';
      }
    }, 0);
  };

  const handleDragEnd = () => {
    if (dragNodeRef.current) {
      dragNodeRef.current.style.opacity = '1';
    }
    setDraggedIndex(null);
    setDragOverIndex(null);
    dragNodeRef.current = null;
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedIndex !== null && draggedIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>, dropIndex: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDragOverIndex(null);
      return;
    }

    const newFields = [...reorderFields];
    const [draggedItem] = newFields.splice(draggedIndex, 1);
    newFields.splice(dropIndex, 0, draggedItem);
    setReorderFields(newFields);
    setDragOverIndex(null);
  };

  const handleSave = () => {
    onSave(reorderFields);
  };

  const handleReset = () => {
    setReorderFields(fields);
    onReset();
  };

  return (
    <Modal
      title={title}
      open={open}
      onOk={handleSave}
      onCancel={onCancel}
      width={600}
      okText="Save Order"
      confirmLoading={saving}
      okButtonProps={{ disabled: saving }}
      cancelButtonProps={{ disabled: saving }}
      footer={[
        <Button key="reset" onClick={handleReset} disabled={saving}>
          Reset to Default
        </Button>,
        <Button key="cancel" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>,
        <Button key="save" type="primary" onClick={handleSave} loading={saving} disabled={saving}>
          Save Order
        </Button>,
      ]}
    >
      <div style={{ marginBottom: 16 }}>
        <Text type="secondary">Drag fields to reorder them. Click "Save Order" to apply changes.</Text>
      </div>
      <div style={{ 
        maxHeight: 400, 
        overflowY: 'auto',
        border: '1px solid #d9d9d9',
        borderRadius: 8,
        padding: 8,
      }}>
        {reorderFields.map((field, index) => (
          <div
            key={field.db_name}
            draggable
            onDragStart={(e) => handleDragStart(e, index)}
            onDragEnd={handleDragEnd}
            onDragOver={(e) => handleDragOver(e, index)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, index)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '12px 16px',
              marginBottom: index < reorderFields.length - 1 ? 4 : 0,
              background: dragOverIndex === index ? '#e6f4ff' : '#fafafa',
              borderRadius: 6,
              cursor: 'grab',
              border: dragOverIndex === index ? '2px dashed #1890ff' : '1px solid #f0f0f0',
              transition: 'all 0.2s ease',
              userSelect: 'none',
            }}
          >
            <HolderOutlined style={{ color: '#999', fontSize: 16 }} />
            <div style={{ minWidth: 28, textAlign: 'center', fontSize: 12, fontWeight: 600, color: '#666' }}>
              {index + 1}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {getFieldIcon(field.db_name, { size: 20 })}
            </div>
            <div style={{ flex: 1 }}>
              <Text strong>{field.display_name}</Text>
              {field.unit && (
                <Text type="secondary" style={{ marginLeft: 8 }}>({field.unit})</Text>
              )}
            </div>
          </div>
        ))}
      </div>
    </Modal>
  );
};
